/**
 * ISO 3166-1 alpha-2 country codes.
 * Used for the company country dropdown and Stripe Tax integration.
 * Stripe Tax requires ISO 3166-1 alpha-2 codes on the customer address.
 */
export const COUNTRY_CODES = [
  "AD", "AE", "AF", "AG", "AI", "AL", "AM", "AO", "AQ", "AR",
  "AS", "AT", "AU", "AW", "AX", "AZ", "BA", "BB", "BD", "BE",
  "BF", "BG", "BH", "BI", "BJ", "BL", "BM", "BN", "BO", "BQ",
  "BR", "BS", "BT", "BV", "BW", "BY", "BZ", "CA", "CC", "CD",
  "CF", "CG", "CH", "CI", "CK", "CL", "CM", "CN", "CO", "CR",
  "CU", "CV", "CW", "CX", "CY", "CZ", "DE", "DJ", "DK", "DM",
  "DO", "DZ", "EC", "EE", "EG", "EH", "ER", "ES", "ET", "FI",
  "FJ", "FK", "FM", "FO", "FR", "GA", "GB", "GD", "GE", "GF",
  "GG", "GH", "GI", "GL", "GM", "GN", "GP", "GQ", "GR", "GS",
  "GT", "GU", "GW", "GY", "HK", "HM", "HN", "HR", "HT", "HU",
  "ID", "IE", "IL", "IM", "IN", "IO", "IQ", "IR", "IS", "IT",
  "JE", "JM", "JO", "JP", "KE", "KG", "KH", "KI", "KM", "KN",
  "KP", "KR", "KW", "KY", "KZ", "LA", "LB", "LC", "LI", "LK",
  "LR", "LS", "LT", "LU", "LV", "LY", "MA", "MC", "MD", "ME",
  "MF", "MG", "MH", "MK", "ML", "MM", "MN", "MO", "MP", "MQ",
  "MR", "MS", "MT", "MU", "MV", "MW", "MX", "MY", "MZ", "NA",
  "NC", "NE", "NF", "NG", "NI", "NL", "NO", "NP", "NR", "NU",
  "NZ", "OM", "PA", "PE", "PF", "PG", "PH", "PK", "PL", "PM",
  "PN", "PR", "PS", "PT", "PW", "PY", "QA", "RE", "RO", "RS",
  "RU", "RW", "SA", "SB", "SC", "SD", "SE", "SG", "SH", "SI",
  "SJ", "SK", "SL", "SM", "SN", "SO", "SR", "SS", "ST", "SV",
  "SX", "SY", "SZ", "TC", "TD", "TF", "TG", "TH", "TJ", "TK",
  "TL", "TM", "TN", "TO", "TR", "TT", "TV", "TW", "TZ", "UA",
  "UG", "UM", "US", "UY", "UZ", "VA", "VC", "VE", "VG", "VI",
  "VN", "VU", "WF", "WS", "XK", "YE", "YT", "ZA", "ZM", "ZW",
] as const;

export type CountryCode = (typeof COUNTRY_CODES)[number];

/**
 * Returns a sorted list of { code, name } pairs for all countries,
 * localized using the browser's built-in Intl.DisplayNames API.
 *
 * @param locale - BCP-47 locale string (e.g., "de-DE", "en-US")
 */
export function getLocalizedCountries(locale: string): { code: string; name: string }[] {
  const displayNames = new Intl.DisplayNames([locale], { type: "region" });

  return COUNTRY_CODES
    .map((code) => ({
      code,
      name: displayNames.of(code) ?? code,
    }))
    .sort((a, b) => a.name.localeCompare(b.name, locale));
}

/**
 * Returns a sorted list of display names for use in OptionInput.
 */
export function getCountryDisplayNames(locale: string): string[] {
  return getLocalizedCountries(locale).map((c) => c.name);
}

/**
 * Resolves a country display name back to its ISO code.
 */
export function getCountryCodeFromName(name: string, locale: string): string | undefined {
  const countries = getLocalizedCountries(locale);
  return countries.find((c) => c.name === name)?.code;
}

/**
 * Resolves an ISO country code to its localized display name.
 */
export function getCountryNameFromCode(code: string, locale: string): string {
  const displayNames = new Intl.DisplayNames([locale], { type: "region" });
  // A stored `country` is a plain string (CompanyContent resolves any
  // 2-character one through here); `of()` throws a RangeError on a value that
  // is not a region code ⇒ show it as-is instead (PB-FB-H3-MAILNOLANG sweep).
  try {
    return displayNames.of(code) ?? code;
  } catch {
    return code;
  }
}

/**
 * Extracts the country code from a BCP-47 locale string.
 * e.g., "de-DE" → "DE", "en-US" → "US", "fr" → undefined
 */
export function getCountryFromLocale(locale: string): string | undefined {
  const parts = locale.split("-");
  if (parts.length >= 2) {
    return parts[parts.length - 1].toUpperCase();
  }
  return undefined;
}
